import { Component, Injector, Input, OnInit, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { RRICheckboxOption } from '../../interfaces/checkbox-option.interface';
import { RRIInput } from '../../interfaces/input-settings.interface';

@Component({
  selector: 'rr-checkbox-field',
  templateUrl: './checkbox-field.component.html',
  styleUrls: ['./checkbox-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CheckboxFieldComponent),
      multi: true,
    },
  ],
})
export class CheckboxFieldComponent<T>
  implements OnInit, ControlValueAccessor, Validator
{
  public data: Array<RRICheckboxOption<T>> = [];
  public control: NgControl;
  public onChangeFn: any = () => {};
  public onTouchFn: any = () => {};
  private errorMessages = new Map<string, () => string>();

  @Input() settings: RRIInput<RRICheckboxOption<T>[]>;
  @Input() options: RRICheckboxOption<T>[];
  @Input() customErrorMessages: Map<string, () => string>;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() highlighOnSelected: boolean = false;
  @Input() checkboxLayoutRight: boolean = false;

  constructor(private inj: Injector) {
    this.errorMessages.set(
      'required',
      () => `${this.settings.label} is required.`,
    );
  }

  ngOnInit(): void {
    this.control = this.inj.get(NgControl, null);

    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
  }

  public isChecked(option: RRICheckboxOption<T>): boolean {
    if (this.data && this.data.length) {
      return this.data.find((d) => d.id === option.id) !== undefined;
    }

    return false;
  }

  public onCheckboxChange(selectedOption: RRICheckboxOption<T>): void {
    this.checkUncheckBox(selectedOption);
    this.onChange();
  }

  public writeValue(selectedOptions: RRICheckboxOption<T>[]): void {
    this.data = selectedOptions;
    if (this.options) {
      this.options.forEach((o: RRICheckboxOption<T>) => {
        if (this.data && this.data.find((d) => d.id === o.id) !== undefined) {
          o.selected = true;
        } else {
          o.selected = false;
        }
      });
    }
  }

  private checkUncheckBox(selectedOption: RRICheckboxOption<T>) {
    if (selectedOption && typeof selectedOption.id !== 'undefined') {
      if (!selectedOption.selected) {
        if (this.data) {
          this.data = this.data.filter(
            (option) => option.id !== selectedOption.id,
          );
        } else {
          this.data = [];
        }
      } else {
        if (this.data) {
          this.data = this.data.concat([selectedOption]);
        } else {
          this.data = [selectedOption];
        }
      }
    }
  }

  registerOnChange(fn: (rating: number) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }
  public onChange() {
    this.onChangeFn(this.data);
  }
  public onTouched() {
    this.onTouchFn();
  }
  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }
  public validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }
}
